/**
 * Funds List Page — Enterprise Data Table
 *
 * Sortable table with columns: Name, Code, SFDR, Status, Currency, Target Size, Created
 * "Create Fund" button opens FundCreateDialog.
 * Row click navigates to fund detail.
 * Filterable by status and SFDR category.
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  ArrowUpDown,
  Search,
  Filter,
  ChevronDown,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { FundCreateDialog } from './FundCreateDialog';
import { useTranslation } from '@/i18n';
import { formatStatus } from '@/utils/formatStatus';
import { formatCurrencyCompact } from '@/utils/formatCurrency';
import type { Fund, FundStatus, SFDRClassification } from '@/types';

type SortKey = 'name' | 'fund_code' | 'sfdr_classification' | 'status' | 'currency' | 'target_size' | 'created_at';
type SortDir = 'asc' | 'desc';

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'info' | 'muted'> = {
  fundraising: 'info',
  investing: 'success',
  harvesting: 'warning',
  closed: 'muted',
};

const SFDR_LABELS: Record<string, string> = {
  article_6: 'Art. 6',
  article_8: 'Art. 8',
  article_9: 'Art. 9',
};

const SFDR_VARIANTS: Record<string, 'default' | 'warning' | 'success'> = {
  article_6: 'default',
  article_8: 'warning',
  article_9: 'success',
};

export function FundsListPage() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FundStatus | ''>('');
  const [sfdrFilter, setSfdrFilter] = useState<SFDRClassification | ''>('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });

  const funds = fundsQuery.data ?? [];

  const toggleSort = useCallback((key: SortKey) => {
    setSortDir((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  }, [sortKey]);

  const filtered = useMemo(() => {
    let list = Array.isArray(funds) ? [...funds] : [];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          (f.fund_code ?? '').toLowerCase().includes(q)
      );
    }

    if (statusFilter) {
      list = list.filter((f) => f.status === statusFilter);
    }

    if (sfdrFilter) {
      const sfdr = sfdrFilter;
      list = list.filter((f) => (f.sfdr_category ?? f.sfdr_classification) === sfdr);
    }

    list.sort((a, b) => {
      const aVal = getSortValue(a, sortKey);
      const bVal = getSortValue(b, sortKey);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [funds, search, statusFilter, sfdrFilter, sortKey, sortDir]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">{t('page.funds')}</h1>
          <p className="text-xs text-ic-text-tertiary">
            {t('count.fundsTotal').replace('{count}', String(funds.length))}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('action.createFund')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
          <input
            type="text"
            placeholder={t('action.search') + '...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-ic-border bg-ic-input py-2 pl-9 pr-3 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
          />
        </div>

        <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-ic-text-tertiary" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FundStatus | '')}
            className="appearance-none rounded-md border border-ic-border bg-ic-input py-2 pl-7 pr-7 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
          >
            <option value="">{t('filter.allStatus')}</option>
            <option value="fundraising">{t('status.fundraising')}</option>
            <option value="investing">{t('status.investing')}</option>
            <option value="harvesting">{t('status.harvesting')}</option>
            <option value="closed">{t('status.closed')}</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
        </div>

        <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-ic-text-tertiary" />
          <select
            value={sfdrFilter}
            onChange={(e) => setSfdrFilter(e.target.value as SFDRClassification | '')}
            className="appearance-none rounded-md border border-ic-border bg-ic-input py-2 pl-7 pr-7 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
          >
            <option value="">{t('filter.allSfdr')}</option>
            <option value="article_6">Art. 6</option>
            <option value="article_8">Art. 8</option>
            <option value="article_9">Art. 9</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
        {fundsQuery.isLoading ? (
          <TableSkeleton rows={5} cols={7} />
        ) : fundsQuery.isError ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-red-400">{t('error.loadFailed')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-ic-text-tertiary">
              {funds.length === 0 ? t('empty.noFundsYet') : t('empty.noFundsMatch')}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs ic-table">
            <thead>
              <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                <SortableHeader label={t('table.name')} sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableHeader label={t('table.code')} sortKey="fund_code" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableHeader label="SFDR" sortKey="sfdr_classification" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableHeader label={t('table.status')} sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableHeader label={t('table.currency')} sortKey="currency" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableHeader label={t('form.targetSize')} sortKey="target_size" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                <SortableHeader label={t('table.createdAt')} sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((fund) => (
                <tr
                  key={fund.id}
                  onClick={() => navigate(`/funds/${fund.id}`)}
                  className="border-b border-ic-border/50 last:border-0 cursor-pointer hover:bg-ic-hover/50 transition-colors"
                >
                  <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">
                    {fund.name}
                  </td>
                  <td className="py-2.5 px-4 font-mono text-xs text-ic-text-secondary">
                    {fund.fund_code ?? '--'}
                  </td>
                  <td className="py-2.5 px-4">
                    <StatusBadge
                      label={SFDR_LABELS[fund.sfdr_category ?? fund.sfdr_classification] ?? '--'}
                      variant={SFDR_VARIANTS[fund.sfdr_category ?? fund.sfdr_classification] ?? 'default'}
                    />
                  </td>
                  <td className="py-2.5 px-4">
                    <StatusBadge
                      label={formatStatus(fund.status, t)}
                      variant={STATUS_VARIANTS[fund.status] ?? 'default'}
                    />
                  </td>
                  <td className="py-2.5 px-4 text-xs text-ic-text-secondary">
                    {fund.base_currency ?? fund.currency}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-secondary">
                    {formatCurrencyCompact(fund.target_size ?? fund.fund_size, fund.base_currency ?? fund.currency ?? 'USD', language)}
                  </td>
                  <td className="py-2.5 px-4 text-right text-xs text-ic-text-tertiary">
                    {fund.created_at ? format(parseISO(fund.created_at), 'dd MMM yyyy') : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Dialog */}
      {showCreate && (
        <FundCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void fundsQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function getSortValue(fund: Fund, key: SortKey): string | number {
  switch (key) {
    case 'name': return (fund.name ?? '').toLowerCase();
    case 'fund_code': return (fund.fund_code ?? '').toLowerCase();
    case 'sfdr_classification': return fund.sfdr_category ?? fund.sfdr_classification ?? '';
    case 'status': return fund.status ?? '';
    case 'currency': return fund.base_currency ?? fund.currency ?? '';
    case 'target_size': return fund.target_size ?? fund.fund_size ?? 0;
    case 'created_at': return fund.created_at ?? '';
    default: return '';
  }
}

// formatAmount removed — replaced by formatCurrency utility

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}

function SortableHeader({ label, sortKey: key, current, dir, onSort, className = '' }: SortableHeaderProps) {
  const isActive = current === key;
  return (
    <th
      className={`px-4 pb-2 pt-3 font-medium cursor-pointer select-none hover:text-ic-text-secondary ${className}`}
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${isActive ? 'text-ic-accent' : 'opacity-30'}`} />
        {isActive && (
          <span className="text-ic-accent">{dir === 'asc' ? '\u2191' : '\u2193'}</span>
        )}
      </span>
    </th>
  );
}

function TableSkeleton({ rows, cols }: { rows: number; cols: number }) {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="h-6 flex-1 animate-pulse rounded bg-slate-700/40" />
          ))}
        </div>
      ))}
    </div>
  );
}
